import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CrmLayout from "@/components/feature/CrmLayout";
import { useToast } from "@/components/base/Toast";
import WhatsAppStatsBar from "./components/WhatsAppStatsBar";
import ConversationList from "./components/ConversationList";
import type { WhatsAppConversation, WhatsAppMessage } from "./components/ConversationList";
import ChatWindow from "./components/ChatWindow";
import type { AgentItem } from "./components/ChatWindow";
import LeadContextPanel from "./components/LeadContextPanel";
import { fetchApi } from "@/services/apiService";

export default function WhatsAppPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlLeadId = searchParams.get("leadId");
  const urlChatId = searchParams.get("chatId");
  const { showToast } = useToast();

  const [conversations, setConversations] = useState<WhatsAppConversation[]>([]);
  const [selectedId, setSelectedId] = useState<string | number>("");
  const initialUrlHandled = useRef(false);
  const [loading, setLoading] = useState(true);
  const [statsData, setStatsData] = useState<{
    totalConversations?: number;
    unread?: number;
    avgResponseTime?: string;
    resolvedToday?: number;
  }>({
    totalConversations: 0,
    unread: 0,
    avgResponseTime: "2.4m",
    resolvedToday: 0,
  });

  const [agentsList, setAgentsList] = useState<AgentItem[]>([]);
  const [leadPanelOpen, setLeadPanelOpen] = useState(false);
  const [manageDrawerOpen, setManageDrawerOpen] = useState(false);

  // Bulk reassign state
  const [bulkFromAgent, setBulkFromAgent] = useState("");
  const [bulkToAgent, setBulkToAgent] = useState("");
  const [bulkTransferMode, setBulkTransferMode] = useState<"all" | "open" | "unresolved">("all");
  const [bulkReason, setBulkReason] = useState("");
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [assignAllTargetAgent, setAssignAllTargetAgent] = useState("");
  const [individualAssignTargets, setIndividualAssignTargets] = useState<Record<string | number, string>>({});
  const [assigningChatId, setAssigningChatId] = useState<string | number | null>(null);
  const [assigningAll, setAssigningAll] = useState(false);

  // Fetch initial chats, stats, and agents
  const loadChats = useCallback(async () => {
    try {
      const res = await fetchApi<any[]>('/whatsapp/chats', undefined, []);
      let items: WhatsAppConversation[] = Array.isArray(res) ? res : (res as any)?.data || [];

      // Check if URL specifies a leadId or chatId
      let matchedId: string | number = "";
      if (urlChatId) {
        const found = items.find((c) => String(c.id) === String(urlChatId));
        if (found) matchedId = found.id;
      }

      if (!matchedId && urlLeadId) {
        const found = items.find((c) => c.leadId && String(c.leadId) === String(urlLeadId));
        if (found) {
          matchedId = found.id;
        } else {
          // Attempt to fetch lead info and start chat
          try {
            const lead = await fetchApi<any>(`/leads/${urlLeadId}`, undefined, null);
            if (lead && lead.name) {
              const newChatRes = await fetchApi<any>('/whatsapp/chats', {
                method: 'POST',
                body: JSON.stringify({
                  lead_id: lead.id,
                  contact_name: lead.name,
                  phone: lead.phone || '',
                  stage: lead.stage || 'New Inquiry',
                  deal_value: lead.value || '₹0',
                  assigned_agent: lead.assigned || 'Rajesh Kumar',
                  initial_message: `Started chat with ${lead.name}`,
                }),
              });
              const created = newChatRes?.data || newChatRes;
              if (created && created.id) {
                items = [created, ...items];
                matchedId = created.id;
              }
            }
          } catch (e) {
            console.warn("Could not auto-create chat for lead:", e);
          }
        }
      }

      setConversations(items);

      if (!initialUrlHandled.current && matchedId) {
        initialUrlHandled.current = true;
        setSelectedId(matchedId);
      } else {
        setSelectedId((prev) => {
          if (prev && items.some((c) => String(c.id) === String(prev))) {
            return prev;
          }
          return items.length > 0 ? items[0].id : "";
        });
      }
    } catch (err) {
      console.error("Failed to load WhatsApp chats:", err);
    }
  }, [urlLeadId, urlChatId]);

  const loadStats = useCallback(async () => {
    try {
      const res = await fetchApi<any>('/whatsapp/stats', undefined, null);
      const data = res?.data || res;
      if (data) {
        setStatsData({
          totalConversations: data.totalChats ?? 0,
          unread: data.unreadTotal ?? 0,
          avgResponseTime: data.avgResponseTime || "2.4m",
          resolvedToday: data.resolvedChats ?? 0,
        });
      }
    } catch {
      // compute from conversations
      setStatsData((prev) => ({
        ...prev,
        totalConversations: conversations.length,
        unread: conversations.reduce((acc, c) => acc + (c.unread || 0), 0),
      }));
    }
  }, [conversations.length]);

  const loadAgents = useCallback(async () => {
    try {
      let list: AgentItem[] = [];

      // 1. Try dedicated WhatsApp agents API
      try {
        const waRes = await fetchApi<any>('/whatsapp/agents', undefined, null);
        const waData = Array.isArray(waRes) ? waRes : waRes?.data || [];
        if (Array.isArray(waData) && waData.length > 0) {
          list = waData.map((u: any) => ({
            id: String(u.id),
            name: u.name,
            initials: u.initials || (u.name || "A").slice(0, 2).toUpperCase(),
            role: u.role || "Sales Agent",
            active: true,
            openChats: u.openChats || 0,
          }));
        }
      } catch (err) {
        console.warn("Could not load /whatsapp/agents, trying company users:", err);
      }

      // 2. Fallback to /auth/company-user/all if empty
      if (list.length === 0) {
        const res = await fetchApi<any>('/auth/company-user/all', undefined, null);
        const userList = Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data?.items)
          ? res.data.items
          : Array.isArray(res?.data)
          ? res.data
          : [];

        userList.forEach((u: any, idx: number) => {
          const name = u.name || u.fullName || u.username;
          if (name) {
            list.push({
              id: String(u.id || idx + 1),
              name,
              initials: name.split(" ").map((n: string) => n[0]).join("").toUpperCase(),
              role: u.role || "Sales Agent",
              active: true,
              openChats: u.leadCount || 0,
            });
          }
        });
      }

      if (list.length > 0) {
        setAgentsList(list);
      } else {
        setAgentsList([
          { id: "1", name: "team", initials: "TM", role: "Sales Director", active: true, openChats: 0 },
          { id: "2", name: "jon", initials: "JN", role: "Owner / CEO", active: true, openChats: 0 },
          { id: "3", name: "AASIB", initials: "AS", role: "super_admin", active: true, openChats: 0 },
          { id: "4", name: "deepak", initials: "DP", role: "Sales Director", active: true, openChats: 0 },
          { id: "5", name: "akash", initials: "AK", role: "Sales Manager", active: true, openChats: 0 },
        ]);
      }
    } catch {
      setAgentsList([
        { id: "1", name: "team", initials: "TM", role: "Sales Director", active: true, openChats: 0 },
        { id: "2", name: "jon", initials: "JN", role: "Owner / CEO", active: true, openChats: 0 },
        { id: "3", name: "AASIB", initials: "AS", role: "super_admin", active: true, openChats: 0 },
        { id: "4", name: "deepak", initials: "DP", role: "Sales Director", active: true, openChats: 0 },
        { id: "5", name: "akash", initials: "AK", role: "Sales Manager", active: true, openChats: 0 },
      ]);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadChats(), loadAgents(), loadStats()]).finally(() => setLoading(false));
  }, [loadChats, loadAgents, loadStats]);

  // Periodic polling to fetch incoming messages in real-time
  useEffect(() => {
    const interval = setInterval(() => {
      loadChats();
      loadStats();
    }, 4000);
    return () => clearInterval(interval);
  }, [loadChats, loadStats]);

  const selected = conversations.find((c) => String(c.id) === String(selectedId)) || conversations[0];

  const handleSelect = useCallback((conv: WhatsAppConversation) => {
    setSelectedId(conv.id);
    initialUrlHandled.current = true;

    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('leadId') || url.searchParams.has('chatId')) {
        url.searchParams.delete('leadId');
        url.searchParams.delete('chatId');
        window.history.replaceState(null, '', url.pathname + (url.search ? url.search : ''));
      }
    } catch {}

    setConversations((prev) =>
      prev.map((c) => (String(c.id) === String(conv.id) ? { ...c, unread: 0 } : c))
    );
    if (conv.unread > 0) {
      fetchApi(`/whatsapp/chats/${conv.id}/stage`, {
        method: 'PATCH',
        body: JSON.stringify({ unread_count: 0 }),
      }).catch(() => {});
    }
  }, []);

  const handleSendMessage = useCallback(
    async (text: string) => {
      const now = new Date();
      const timeStr = now.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

      const newMsg: WhatsAppMessage = {
        id: Date.now(),
        direction: "outgoing",
        text,
        time: timeStr,
        read: true,
      };

      try {
        const currentChat = conversations.find((c) => String(c.id) === String(selectedId));
        const res = await fetchApi<any>('/whatsapp/messages', {
          method: 'POST',
          body: JSON.stringify({
            chat_id: selectedId,
            text,
            sender: 'me',
            phone: currentChat?.phone || ''
          })
        });

        if (res?.metaDelivery) {
          if (res.metaDelivery.success) {
            showToast(res.metaDelivery.note || "WhatsApp message delivered via Meta Cloud API!", "success");
          } else {
            console.warn("Meta Cloud API delivery status:", res.metaDelivery);
            showToast(res.metaDelivery.error || "Meta WhatsApp delivery failed", "warning");
          }
        } else {
          showToast("Message sent to lead", "success");
        }
      } catch (err: any) {
        console.error("Error sending WhatsApp message to backend:", err);
        showToast("Error sending message: " + (err?.message || "Failed"), "error");
      }

      setConversations((prev) =>
        prev.map((c) =>
          String(c.id) === String(selectedId)
            ? {
                ...c,
                messages: [...(c.messages || []), newMsg],
                lastMessage: text,
                lastMessageTime: timeStr,
              }
            : c
        )
      );
    },
    [selectedId]
  );

  const handleAssignAgent = useCallback(async (convId: number | string, agent: AgentItem) => {
    try {
      await fetchApi(`/whatsapp/chats/${convId}/assign`, {
        method: 'PATCH',
        body: JSON.stringify({
          toAgent: agent.name,
          reason: 'Manual assignment',
        }),
      });
    } catch (err) {
      console.error("Failed to assign agent in DB:", err);
    }

    setConversations((prev) =>
      prev.map((c) =>
        String(c.id) === String(convId)
          ? {
              ...c,
              assigned: agent.name,
              assignedAgentId: agent.id,
              assignedAgentName: agent.name,
              assignedAgentInitials: agent.initials,
              assignedAgentActive: true,
            }
          : c
      )
    );
  }, []);

  const handleReassignAgent = useCallback(
    async (convId: number | string, fromAgent: string, toAgent: AgentItem, reason: string) => {
      const now = new Date();
      const timestamp = now.toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

      try {
        await fetchApi(`/whatsapp/chats/${convId}/assign`, {
          method: 'PATCH',
          body: JSON.stringify({
            toAgent: toAgent.name,
            fromAgent,
            reason,
          }),
        });
      } catch (err) {
        console.error("Failed to reassign agent in DB:", err);
      }

      setConversations((prev) =>
        prev.map((c) =>
          String(c.id) === String(convId)
            ? {
                ...c,
                assigned: toAgent.name,
                assignedAgentId: toAgent.id,
                assignedAgentName: toAgent.name,
                assignedAgentInitials: toAgent.initials,
                assignedAgentActive: true,
                reassignedFrom: fromAgent,
                reassignReason: reason || null,
                assignmentHistory: [
                  ...(c.assignmentHistory || []),
                  {
                    fromAgent,
                    toAgent: toAgent.name,
                    reason: reason || "No reason provided",
                    timestamp,
                  },
                ],
              }
            : c
        )
      );
    },
    []
  );

  const handleMarkResolved = useCallback(async (convId: number | string) => {
    try {
      await fetchApi(`/whatsapp/chats/${convId}/stage`, {
        method: 'PATCH',
        body: JSON.stringify({ stage: 'Closed Won' }),
      });
    } catch {}

    setConversations((prev) =>
      prev.map((c) =>
        String(c.id) === String(convId) ? { ...c, stage: "Closed Won" } : c
      )
    );
  }, []);

  const handleStartLeadChat = useCallback(async (lead: any) => {
    const existing = conversations.find(
      (c) =>
        (c.leadId && String(c.leadId) === String(lead.id)) ||
        (c.phone && lead.phone && c.phone.replace(/\D/g, "").slice(-10) === lead.phone.replace(/\D/g, "").slice(-10))
    );

    if (existing) {
      setSelectedId(existing.id);
      showToast(`Selected chat with ${lead.name}`, "info");
      return;
    }

    try {
      const res = await fetchApi<any>('/whatsapp/chats', {
        method: 'POST',
        body: JSON.stringify({
          lead_id: lead.id,
          contact_name: lead.name,
          phone: lead.phone || '',
          stage: lead.stage || 'New Inquiry',
          deal_value: lead.value || '₹0',
          assigned_agent: lead.assigned || 'Rajesh Kumar',
          initial_message: `Started chat with ${lead.name}`,
        }),
      });

      const newConv = res?.data || res;
      if (newConv && newConv.id) {
        setConversations((prev) => [newConv, ...prev]);
        setSelectedId(newConv.id);
        showToast(`Started WhatsApp conversation with ${lead.name}`, "success");
      }
    } catch (err) {
      console.error("Error creating chat for lead:", err);
      showToast("Could not start chat for this lead", "error");
    }
  }, [conversations, showToast]);

  const handleNavigateToLead = useCallback(() => {
    const current = conversations.find((c) => String(c.id) === String(selectedId)) || selected;
    if (current?.leadId) {
      navigate(`/leads/${current.leadId}`);
    } else if (current?.phone) {
      const clean = current.phone.replace(/\D/g, "").slice(-10);
      navigate(`/leads?search=${clean}`);
    } else {
      navigate(`/leads`);
    }
  }, [conversations, selectedId, selected, navigate]);

  const toggleLeadPanel = useCallback(() => {
    setLeadPanelOpen((prev) => !prev);
  }, []);

  // Dynamic unassigned calculation & counts
  const unassignedChats = useMemo(() => {
    return conversations.filter((c) => {
      const a = (c.assignedAgentName || c.assigned || "").trim().toLowerCase();
      return !a || a === "unassigned";
    });
  }, [conversations]);

  const getAgentChatCount = useCallback(
    (agentName: string) => {
      if (!agentName) return 0;
      const norm = agentName.trim().toLowerCase();
      if (norm === "unassigned") {
        return unassignedChats.length;
      }
      return conversations.filter((c) => {
        const a = (c.assignedAgentName || c.assigned || "").trim().toLowerCase();
        return a === norm;
      }).length;
    },
    [conversations, unassignedChats.length]
  );

  const bulkModeCounts = useMemo(() => {
    if (!bulkFromAgent) return { all: 0, open: 0, unresolved: 0 };
    const norm = bulkFromAgent.trim().toLowerCase();
    const isUnassigned = norm === "unassigned";
    const matching = conversations.filter((c) => {
      const a = (c.assignedAgentName || c.assigned || "").trim().toLowerCase();
      return isUnassigned ? (!a || a === "unassigned") : (a === norm);
    });
    const resolvedStages = new Set(["Closed Won", "Lost"]);
    const unresolvedStages = new Set(["New Inquiry", "Contacted", "Site Visit Scheduled", "Negotiation"]);
    return {
      all: matching.length,
      open: matching.filter((c) => !resolvedStages.has(c.stage || "")).length,
      unresolved: matching.filter((c) => unresolvedStages.has(c.stage || "")).length,
    };
  }, [bulkFromAgent, conversations]);

  const handleBulkReassignConfirm = async () => {
    if (!bulkFromAgent || !bulkToAgent) return;

    const toAgent = agentsList.find((a) => a.name === bulkToAgent);
    if (!toAgent) return;

    setBulkLoading(true);
    const affectedCount = bulkModeCounts[bulkTransferMode];
    showToast(`Reassigning ${affectedCount} chats in database...`, "info");

    try {
      const res = await fetchApi<any>('/whatsapp/chats/bulk-reassign', {
        method: 'POST',
        body: JSON.stringify({
          fromAgent: bulkFromAgent,
          toAgent: bulkToAgent,
          transferMode: bulkTransferMode,
          reason: bulkReason || 'Bulk reassignment from Manage Conversations',
        }),
      });

      const count = res?.count ?? affectedCount;

      const isFromUnassigned = bulkFromAgent.toLowerCase() === "unassigned";
      const resolvedStages = new Set(["Closed Won", "Lost"]);
      const unresolvedStages = new Set(["New Inquiry", "Contacted", "Site Visit Scheduled", "Negotiation"]);
      const now = new Date();
      const timestamp = now.toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

      setConversations((prev) =>
        prev.map((c) => {
          const a = (c.assignedAgentName || c.assigned || "").trim().toLowerCase();
          const matches = isFromUnassigned ? (!a || a === "unassigned") : (a === bulkFromAgent.toLowerCase());
          if (!matches) return c;

          if (bulkTransferMode === "open" && resolvedStages.has(c.stage || "")) return c;
          if (bulkTransferMode === "unresolved" && !unresolvedStages.has(c.stage || "")) return c;

          return {
            ...c,
            assigned: toAgent.name,
            assignedAgentId: toAgent.id,
            assignedAgentName: toAgent.name,
            assignedAgentInitials: toAgent.initials,
            assignedAgentActive: true,
            reassignedFrom: bulkFromAgent,
            reassignReason: bulkReason || null,
            assignmentHistory: [
              ...(c.assignmentHistory || []),
              {
                fromAgent: bulkFromAgent,
                toAgent: toAgent.name,
                reason: bulkReason || "Bulk reassignment",
                timestamp,
              },
            ],
          };
        })
      );

      showToast(`${count} chats successfully reassigned to ${toAgent.name}!`, "success");
      setBulkConfirmOpen(false);
      setBulkFromAgent("");
      setBulkToAgent("");
      setBulkReason("");
      loadChats();
      loadStats();
      loadAgents();
    } catch (err: any) {
      console.error("Bulk reassign error:", err);
      showToast(err?.message || "Bulk reassignment failed", "error");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleQuickAssignUnassigned = async (convId: number | string, agentName: string) => {
    if (!agentName) {
      showToast("Please select an agent to assign", "warning");
      return;
    }
    const agent = agentsList.find((a) => a.name === agentName);
    if (!agent) return;

    setAssigningChatId(convId);
    try {
      await handleAssignAgent(convId, agent);
      showToast(`Chat assigned to ${agentName}`, "success");
      setIndividualAssignTargets((prev) => {
        const next = { ...prev };
        delete next[convId];
        return next;
      });
      loadStats();
      loadAgents();
    } catch (err: any) {
      showToast(err?.message || "Failed to assign chat", "error");
    } finally {
      setAssigningChatId(null);
    }
  };

  const handleAssignAllUnassigned = async () => {
    if (!assignAllTargetAgent) return;
    const targetAgent = agentsList.find((a) => a.name === assignAllTargetAgent);
    if (!targetAgent) return;
    if (unassignedChats.length === 0) {
      showToast("No unassigned chats to assign", "info");
      return;
    }

    setAssigningAll(true);
    try {
      const chatIds = unassignedChats.map((c) => c.id);
      await fetchApi('/whatsapp/chats/bulk-assign', {
        method: 'POST',
        body: JSON.stringify({
          chatIds,
          toAgent: targetAgent.name,
          reason: 'Assigned all unassigned chats from Manage drawer',
        }),
      });

      setConversations((prev) =>
        prev.map((c) => {
          const a = (c.assignedAgentName || c.assigned || "").trim().toLowerCase();
          if (!a || a === "unassigned") {
            return {
              ...c,
              assigned: targetAgent.name,
              assignedAgentId: targetAgent.id,
              assignedAgentName: targetAgent.name,
              assignedAgentInitials: targetAgent.initials,
              assignedAgentActive: true,
            };
          }
          return c;
        })
      );

      showToast(`All ${chatIds.length} unassigned chats assigned to ${targetAgent.name}!`, "success");
      setAssignAllTargetAgent("");
      loadChats();
      loadStats();
      loadAgents();
    } catch (err: any) {
      console.error("Assign all error:", err);
      showToast(err?.message || "Failed to assign all chats", "error");
    } finally {
      setAssigningAll(false);
    }
  };

  return (
    <CrmLayout title="WhatsApp">
      <div className="h-full flex flex-col">
        {/* Stats bar */}
        <WhatsAppStatsBar stats={statsData} />

        {/* Main split area */}
        <div className="flex-1 flex min-h-0">
          {/* Left - Conversation List */}
          <ConversationList
            conversations={conversations}
            selectedId={selectedId}
            onSelect={handleSelect}
            onManageClick={() => setManageDrawerOpen(true)}
            onStartLeadChat={handleStartLeadChat}
          />

          {/* Right - Chat Window */}
          {selected ? (
            <ChatWindow
              conversation={selected}
              onToggleLeadPanel={toggleLeadPanel}
              leadPanelOpen={leadPanelOpen}
              onSendMessage={handleSendMessage}
              onAssignAgent={handleAssignAgent}
              onReassignAgent={handleReassignAgent}
              onMarkResolved={handleMarkResolved}
              agentsList={agentsList}
              onNavigateToLead={handleNavigateToLead}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center bg-gray-50 text-gray-400 text-sm">
              {loading ? (
                <div className="flex items-center gap-2">
                  <i className="ri-loader-4-line animate-spin text-lg" /> Loading WhatsApp chats...
                </div>
              ) : (
                "Select a conversation to start chatting"
              )}
            </div>
          )}

          {/* Lead Context Panel */}
          {leadPanelOpen && selected && (
            <LeadContextPanel
              conversation={selected}
              onClose={() => setLeadPanelOpen(false)}
              onNavigateToLead={handleNavigateToLead}
            />
          )}

          {/* Manage Drawer */}
          {manageDrawerOpen && (
            <>
              <div className="fixed inset-0 z-40 bg-black/20" onClick={() => setManageDrawerOpen(false)} />
              <div className="fixed right-0 top-0 bottom-0 w-[350px] md:w-[380px] z-50 bg-white border-l border-[#e5e7eb] flex flex-col animate-slide-in shadow-2xl overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#e5e7eb] bg-gray-50/70">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                      <i className="ri-settings-4-line text-lg" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-[#111827]">Manage Conversations</h3>
                      <p className="text-[11px] text-gray-500">Bulk reassignment & chat distribution</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setManageDrawerOpen(false)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-200/60 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-lg" />
                  </button>
                </div>

                <div className="p-4 space-y-4">
                  {/* Bulk Reassign */}
                  <div className="bg-white rounded-xl border border-[#e5e7eb] p-4 shadow-xs">
                    <div className="flex items-center gap-2 mb-1">
                      <i className="ri-arrow-left-right-line text-amber-600 text-sm" />
                      <h4 className="text-sm font-semibold text-[#111827]">Bulk Reassign</h4>
                    </div>
                    <p className="text-xs text-[#6b7280] mb-3">
                      Transfer all chats from one agent to another in real-time
                    </p>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">From Agent:</label>
                        <select
                          value={bulkFromAgent}
                          onChange={(e) => {
                            setBulkFromAgent(e.target.value);
                            setBulkToAgent("");
                          }}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 bg-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 cursor-pointer"
                        >
                          <option value="">Select agent...</option>
                          {unassignedChats.length > 0 && (
                            <option value="Unassigned">⚠️ Unassigned ({unassignedChats.length} chats)</option>
                          )}
                          {agentsList.map((a) => (
                            <option key={a.id} value={a.name}>
                              {a.name} ({getAgentChatCount(a.name)} chats)
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">To Agent:</label>
                        <select
                          value={bulkToAgent}
                          onChange={(e) => setBulkToAgent(e.target.value)}
                          disabled={!bulkFromAgent}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 bg-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        >
                          <option value="">Select target agent...</option>
                          {agentsList
                            .filter((a) => a.name.toLowerCase() !== bulkFromAgent.toLowerCase())
                            .map((a) => (
                              <option key={a.id} value={a.name}>
                                {a.name} ({a.role})
                              </option>
                            ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">Transfer:</label>
                        <div className="space-y-1.5 bg-gray-50/70 p-2.5 rounded-lg border border-gray-100">
                          {(["all", "open", "unresolved"] as const).map((mode) => (
                            <label key={mode} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="bulkTransferMode"
                                value={mode}
                                checked={bulkTransferMode === mode}
                                onChange={() => setBulkTransferMode(mode)}
                                className="w-3.5 h-3.5 text-indigo-600 accent-indigo-600 cursor-pointer"
                              />
                              <span className="text-xs text-[#374151]">
                                {mode === "all" && `All chats (${bulkModeCounts.all} total)`}
                                {mode === "open" && `Open chats only (${bulkModeCounts.open})`}
                                {mode === "unresolved" && `Unresolved chats only (${bulkModeCounts.unresolved})`}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Reason <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <input
                          type="text"
                          value={bulkReason}
                          onChange={(e) => setBulkReason(e.target.value)}
                          placeholder="e.g. Agent on leave, re-allocating leads"
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                        />
                      </div>

                      <button
                        onClick={() => setBulkConfirmOpen(true)}
                        disabled={!bulkFromAgent || !bulkToAgent || bulkModeCounts[bulkTransferMode] === 0 || bulkLoading}
                        className="w-full py-2.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-1.5 shadow-xs"
                      >
                        <i className="ri-arrow-right-up-line" />
                        Bulk Reassign {bulkFromAgent && bulkModeCounts[bulkTransferMode] > 0 ? `(${bulkModeCounts[bulkTransferMode]})` : ""}
                      </button>
                    </div>
                  </div>

                  {/* Unassigned Chats */}
                  <div className="bg-white rounded-xl border border-[#e5e7eb] p-4 shadow-xs">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-1.5">
                        <i className="ri-user-unfollow-line text-red-500 text-sm" />
                        <h4 className="text-sm font-semibold text-[#111827]">Unassigned Chats</h4>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        unassignedChats.length > 0 ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"
                      }`}>
                        {unassignedChats.length} unassigned
                      </span>
                    </div>

                    {unassignedChats.length > 0 ? (
                      <div className="space-y-2.5">
                        <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                          {unassignedChats.map((chat) => (
                            <div
                              key={chat.id}
                              className="p-2.5 rounded-lg bg-gray-50 border border-gray-100 hover:border-gray-200 transition-colors"
                            >
                              <div className="flex items-center justify-between gap-1 mb-1.5">
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-semibold text-[#111827] truncate">{chat.name}</p>
                                  <p className="text-[11px] text-[#6b7280] truncate">
                                    {chat.phone || chat.lastMessage || "No phone number"}
                                  </p>
                                </div>
                                {chat.dealValue && chat.dealValue !== "₹0" && (
                                  <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">
                                    {chat.dealValue}
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center gap-1.5 pt-1.5 border-t border-gray-200/60">
                                <select
                                  value={individualAssignTargets[chat.id] || ""}
                                  onChange={(e) =>
                                    setIndividualAssignTargets((prev) => ({
                                      ...prev,
                                      [chat.id]: e.target.value,
                                    }))
                                  }
                                  className="flex-1 px-2 py-1 text-xs rounded border border-gray-200 bg-white text-gray-800 focus:outline-none focus:border-indigo-500 cursor-pointer"
                                >
                                  <option value="">Select agent...</option>
                                  {agentsList.map((a) => (
                                    <option key={a.id} value={a.name}>
                                      {a.name}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  onClick={() =>
                                    handleQuickAssignUnassigned(
                                      chat.id,
                                      individualAssignTargets[chat.id] || agentsList[0]?.name || ""
                                    )
                                  }
                                  disabled={
                                    !individualAssignTargets[chat.id] || assigningChatId === chat.id
                                  }
                                  className="px-2.5 py-1 text-xs font-medium rounded bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
                                >
                                  {assigningChatId === chat.id ? (
                                    <i className="ri-loader-4-line animate-spin text-xs" />
                                  ) : (
                                    "Assign"
                                  )}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Bulk assign all unassigned */}
                        <div className="pt-2 border-t border-[#f1f5f9] flex items-center gap-2">
                          <select
                            value={assignAllTargetAgent}
                            onChange={(e) => setAssignAllTargetAgent(e.target.value)}
                            className="flex-1 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-900 bg-white cursor-pointer focus:outline-none focus:border-indigo-500"
                          >
                            <option value="">Assign all {unassignedChats.length} to...</option>
                            {agentsList.map((a) => (
                              <option key={a.id} value={a.name}>
                                {a.name}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={handleAssignAllUnassigned}
                            disabled={!assignAllTargetAgent || assigningAll}
                            className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium transition-colors whitespace-nowrap disabled:opacity-50 cursor-pointer flex items-center gap-1 shadow-xs"
                          >
                            {assigningAll ? (
                              <i className="ri-loader-4-line animate-spin" />
                            ) : (
                              "Assign All"
                            )}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="py-6 text-center">
                        <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-2">
                          <i className="ri-checkbox-circle-line text-xl" />
                        </div>
                        <p className="text-xs font-medium text-gray-700">All chats are assigned</p>
                        <p className="text-[11px] text-gray-400 mt-0.5">No unassigned WhatsApp conversations</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bulk reassign confirmation modal */}
      {bulkConfirmOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-xs">
          <div className="bg-white rounded-xl w-full max-w-sm shadow-xl animate-slide-in p-6 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 text-amber-600">
                <i className="ri-error-warning-line text-2xl" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#111827]">
                  Reassign {bulkModeCounts[bulkTransferMode]} chats?
                </h3>
                <p className="text-xs text-[#6b7280] mt-0.5">
                  Transfer <strong className="text-gray-900">{bulkModeCounts[bulkTransferMode]}</strong> {bulkTransferMode} chats from{" "}
                  <span className="font-semibold text-gray-900">{bulkFromAgent}</span> to{" "}
                  <span className="font-semibold text-indigo-600">{bulkToAgent}</span>.
                </p>
              </div>
            </div>
            <p className="text-[11px] text-gray-500 bg-amber-50/80 p-2.5 rounded-lg border border-amber-200/60 mb-5">
              💡 All linked CRM leads will automatically have their assigned agent updated to match.
            </p>
            <div className="flex items-center gap-2.5 justify-end">
              <button
                onClick={() => setBulkConfirmOpen(false)}
                disabled={bulkLoading}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkReassignConfirm}
                disabled={bulkLoading}
                className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-sm font-medium text-white transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5 shadow-xs disabled:opacity-50"
              >
                {bulkLoading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" /> Reassigning...
                  </>
                ) : (
                  "Confirm Reassign"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </CrmLayout>
  );
}